import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { FilterBar } from '@/components/common/FilterBar';
import { Select2 } from '@/components/forms/Select2';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';
import { ApiError } from '@/api/client/abpError';
import { filterDataApi } from '@/api/services/filterData';
import { revShareDealsApi } from '@/api/services/revenueShare';
import { YES_NO_OPTIONS, type RevShareConfigDto, type RevShareConfigFilterInput } from '@/api/types/revenueShare';
import { RevShareDealFormModal } from './RevShareDealFormModal';

interface DealFilters {
  platformId: string;
  login: string;
  swapsIncluded: string;
  commissionsIncluded: string;
}

const emptyFilters: DealFilters = { platformId: '', login: '', swapsIncluded: '', commissionsIncluded: '' };

function toInput(f: DealFilters): RevShareConfigFilterInput {
  return {
    platformId: f.platformId === '' ? null : Number(f.platformId),
    login: f.login === '' ? null : Number(f.login),
    swapsIncluded: f.swapsIncluded === '' ? null : f.swapsIncluded === 'true',
    commissionsIncluded: f.commissionsIncluded === '' ? null : f.commissionsIncluded === 'true',
  };
}

const yesNo = (v: boolean) => (v ? 'Yes' : 'No');

export function RevShareDealsPage() {
  const toast = useToast();
  const [draft, setDraft] = useState<DealFilters>(emptyFilters);
  const [applied, setApplied] = useState<DealFilters>(emptyFilters);
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; row?: RevShareConfigDto } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RevShareConfigDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const connectorsList = useQuery({ queryKey: ['rev-share', 'connectors'], queryFn: filterDataApi.getConnectors, staleTime: 5 * 60_000 });
  const dealConnectors = useQuery({ queryKey: ['rev-share', 'deal-connectors'], queryFn: revShareDealsApi.getConnectors, staleTime: 5 * 60_000 });

  const appliedInput = useMemo(() => toInput(applied), [applied]);
  const table = useServerDataTable<RevShareConfigDto, RevShareConfigFilterInput>({
    queryKey: ['rev-share', 'deals'],
    fetcher: (req) => revShareDealsApi.getList(req),
    pageSize: 5,
    extraParams: appliedInput,
  });

  const fetch = () => {
    table.setPageIndex(0);
    setApplied({ ...draft });
  };
  const clear = () => {
    setDraft(emptyFilters);
    table.setPageIndex(0);
    setApplied(emptyFilters);
  };

  const afterSave = (verb: string) => {
    setModal(null);
    toast.success(verb);
    void table.refetch();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await revShareDealsApi.remove(deleteTarget.id);
      toast.success('Profile deleted successfully');
      setDeleteTarget(null);
      void table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete profile');
    } finally {
      setDeleting(false);
    }
  };

  const str = (v: string | number | null) => (v == null ? '' : String(v));

  const fields: React.ReactNode[] = [
    <Select2 key="connector" label="Connector" allowClear placeholder="All" value={str(draft.platformId)} options={connectorsList.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, platformId: v ?? '' }))} />,
    <div key="login">
      <label className="form-label">Login</label>
      <input className="form-control" inputMode="numeric" value={draft.login} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value }))} />
    </div>,
    <Select2 key="swaps" label="Swaps Included" allowClear placeholder="All" searchable={false} value={draft.swapsIncluded} options={YES_NO_OPTIONS} onChange={(v) => setDraft((f) => ({ ...f, swapsIncluded: v ?? '' }))} />,
    <Select2 key="comm" label="Commissions Included" allowClear placeholder="All" searchable={false} value={draft.commissionsIncluded} options={YES_NO_OPTIONS} onChange={(v) => setDraft((f) => ({ ...f, commissionsIncluded: v ?? '' }))} />,
  ];

  return (
    <div className="rev-share-deals-page">
      <PageHeader title="Rev Share Deals" breadcrumb={['Home', 'Rev Share Deals']} />

      <FilterBar
        fields={fields}
        actions={
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={fetch} disabled={table.isFetching}>
              {table.isFetching ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch
            </button>
            <button type="button" className="btn btn-link btn-sm" onClick={clear}>Clear All Filters</button>
          </>
        }
      />

      <DataGrid
        columns={[
          { key: 'identifier', title: 'Identifier', className: 'text-start', sortable: false, value: (r) => r.identifier },
          { key: 'connectorName', title: 'Connector', className: 'text-start', sortable: false, value: (r) => r.connectorName },
          { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login },
          { key: 'periodType', title: 'Period Type', className: 'text-start', value: (r) => r.periodType },
          { key: 'hwmPeriod', title: 'HWM Period', className: 'text-start', value: (r) => r.hwmPeriod },
          { key: 'revSharePercentage', title: 'RevShare %', className: 'text-end', value: (r) => r.revSharePercentage, cell: (r) => formatNumber(r.revSharePercentage, 2) },
          { key: 'highWaterMark', title: 'High Water Mark', className: 'text-end', value: (r) => r.highWaterMark, cell: (r) => formatNumber(r.highWaterMark, 2) },
          { key: 'includeCommissions', title: 'Commissions Included', className: 'text-start', value: (r) => yesNo(r.includeCommissions) },
          { key: 'includeSwaps', title: 'Swaps Included', className: 'text-start', value: (r) => yesNo(r.includeSwaps) },
          { key: 'activeLprsPnlConfiguration', title: 'Active LP RS Profile', className: 'text-start', value: (r) => yesNo(r.activeLprsPnlConfiguration) },
        ] as DataGridColumn<RevShareConfigDto>[]}
        data={table.rows}
        rowKey={(r) => String(r.id)}
        card
        includeExcel
        excelFileName="Rev Share Deals"
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        toolbarStart={
          <button type="button" className="btn btn-sm btn-primary" title="Add new profile" onClick={() => setModal({ mode: 'create' })}>
            <i className="fa fa-plus me-1" />Add
          </button>
        }
        rowActions={(row) => (
          <div className="d-inline-flex gap-1 justify-content-center">
            <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => setModal({ mode: 'edit', row })}>
              <i className="fa fa-edit" />
            </button>
            <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setDeleteTarget(row)}>
              <i className="fa fa-times" />
            </button>
          </div>
        )}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />

      {modal && (
        <RevShareDealFormModal
          mode={modal.mode}
          row={modal.row}
          connectors={dealConnectors.data ?? []}
          onClose={() => setModal(null)}
          onSuccess={afterSave}
        />
      )}

      <ConfirmDialog
        show={!!deleteTarget}
        title="Are You Sure?"
        message="Are you sure you want to proceed with deleting selected row?"
        confirmLabel="Yes"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
